import { Entity } from "../../Entity";
import ContractFactory from "../../Factories/ContractFactory";
import Image from "../../../Image/Image";
import { FactoryInterface } from "../../FactoryInterface";
import { ContractInterface } from "./ContractInterface";
import { PersonInterface } from "../Person/PersonInterface";
import { CompanyInterface } from "../Company/CompanyInterface";
import { UnitInterface } from "../Unit/UnitInterface";
import { ContactForContractInterface } from "../ContactForContract/ContactForContractInterface";
import { AttachmentInterface } from "../Attachment/AttachmentInterface";
import { ChargeTemplateInterface } from "../Charge/ChargeTemplateInterface";
import TypeContainer from "../../../Entity/Entities/Charge/Type/TypeContainer";
import { SubscriptionInterface } from "../Subscription/SubscriptionInterface";
import { MainTenant } from "../ContactForContract/Type/Types/MainTenant";
import SubscriptionTypeContainer from "../../../Entity/Entities/Subscription/SubscriptionType/SubscriptionTypeContainer";
import Store from "../../../Store/Store";
import { AgencyFeeInterface } from "../AgencyFee/AgencyFeeInterface";
import AgencyFeeContainer from "../../../Entity/Entities/AgencyFee/AgencyFeeContainer";
import Translator from "../../../Translation/Translator";

class Contract extends Entity implements ContractInterface {
  constructor(public data) {
    super();
  }

  get nameOfEntity() {
    return Translator.trans("contract");
  }

  get nameForUrl() {
    return "contract";
  }

  get labelForEntity() {
    return "label-contract"
  }

  public getFactory(): FactoryInterface {
    return ContractFactory;
  }

  get id(): number {
    return this.data.id;
  }

  get contractStatusId(): number {
    return this.data.contractStatusId;
  }

  get contractStatus(): string {
    const status = (Object.keys(Store.getConfigurator().getState().app?.contractStatuses || {}))
      .find(key => Store.getConfigurator().getState().app.contractStatuses[key] === this.contractStatusId);

    if (!status) {
      return;
    }

    return status.toLowerCase();
  }

  get amazonFolderName(): string {
    return this.data.amazonFolderName;
  }

  get attachments(): AttachmentInterface[] {
    return this.data.attachments;
  }

  get agencyId(): number {
    return this.data.agencyId;
  }

  get amount(): any {
    return this.data.amount;
  }

  get checkinDate(): Date {
    return this.data.checkinDate;
  }

  get checkoutDate(): Date {
    return this.data.checkoutDate;
  }

  get createdAt(): string {
    return this.data.createdAt;
  }

  get displayName(): string {
    const arr: string[] = [
      this.tenant.displayName || "",
      this.unit && this.unit.displayName ? this.unit.displayName : "",
    ];

    return arr.join(", ");
  }

  get listDisplayName(): string {
    return this.nameOfEntity + ", " + this.displayName;
  }

  get chargeTemplates(): ChargeTemplateInterface[] {
    // We have to return non-default charge templates.
    return this.relations("app.chargeTemplates", "app.chargeTemplatesInContracts", this.data.id)
      .filter((chargeTemplate: ChargeTemplateInterface) => !!chargeTemplate &&
        chargeTemplate.isDefault === false &&
        chargeTemplate.removed === false)
      .sort((template1: ChargeTemplateInterface, template2: ChargeTemplateInterface) => {
        if (template1.creationDate > template2.creationDate) {
          return 1;
        }

        if (template1.creationDate < template2.creationDate) {
          return -1;
        }

        return 0;
      });
  }

  get avatar(): string {
    return this.data.avatar;
  }

  get createdBy(): number {
    return this.data.createdBy;
  }

  get firstDate(): Date {
    return this.data.firstDate;
  }

  get contractFirstDate(): Date {
    return this.data.contractFirstDate;
  }

  get lastDate(): Date | null {
    return this.data.lastDate;
  }

  get finalRentalPeriodDate(): Date | null {
    return this.data.finalRentalPeriodDate;
  }

  get expirationDate(): Date | null {
    return this.data.expirationDate;
  }

  get endDate(): Date | null {
    return this.data.endDate;
  }

  get payChargesUntilDate(): Date | null {
    return this.data.payChargesUntil;
  }

  get mainTenantContactId(): number {
    return this.data.mainTenantContactId;
  }

  get mainTenantCompanyId(): number {
    return this.data.mainTenantCompanyId;
  }

  get tenant(): PersonInterface | CompanyInterface {
    if (this.mainTenantCompanyId) {
      return this.relation("app.companies", this.data.mainTenantCompanyId)[0];
    } else {
      return this.relation("app.persons", this.data.mainTenantContactId)[0];
    }
  }

  get tenantDisplayName(): string {
    return this.data.tenantDisplayName;
  }

  get contractParties(): ContactForContractInterface[] {
    return this.data.contractParties.filter((contactForContract: ContactForContractInterface) => {
      // We don't need to include main Tenant as contact party
      return contactForContract.type.getValue() !== MainTenant.contactForContractTypeId;
    });
  }

  get person(): PersonInterface {
    return this.relation("app.persons", this.data.mainTenantContactId)[0];
  }

  get company(): CompanyInterface {
    return this.relation("app.companies", this.data.mainTenantCompanyId)[0];
  }

  get occupant(): PersonInterface {
    const contactForContract = this.occupants[0];

    if (!!contactForContract) {
      return contactForContract.person;
    }
  }

  get occupants(): ContactForContractInterface[] {
    if (this.contractParties.length === 0) {
      return [];
    }

    // Return the persons except the Tenant.
    return this.contractParties.filter(contract => {
      return contract.contactForContractTypeId !== MainTenant.contactForContractTypeId;
    });
  }

  get subscription(): SubscriptionInterface {
    return this.subscriptionRelation(SubscriptionTypeContainer.pickIdFromLabel("Contracts"), this.data.id);
  }

  get isSubscribed(): boolean {
    return this.subscription !== null;
  }

  get nextContractId(): any {
    return this.data.nextContractId;
  }

  get previousContractId(): any {
    return this.data.previousContractId;
  }

  get unitId(): number {
    return this.data.unitId;
  }

  get unit(): UnitInterface {
    return this.newRelation("units.byId", this.data.unitId)[0];
  }

  public getStatusColor(): string {
    if (this.isSigned || this.isDropped || this.isClosed) {
      return "#43A047";
    }

    if (this.isEnding || this.isExpiring) {
      return "#F5A623";
    }

    return "#F44336";
  }

  get isConcept(): boolean {
    return this.contractStatus === "concept";
  }

  get isSigned(): boolean {
    return this.contractStatus === "signed";
  }

  get isDropped(): boolean {
    return this.contractStatus === "dropped";
  }

  get isExpiring(): boolean {
    return this.contractStatus === "expiring";
  }

  get isEnding(): boolean {
    return this.contractStatus === "ending";
  }

  get isEnded(): boolean {
    return this.contractStatus === "ended";
  }

  get isClosed(): boolean {
    return this.contractStatus === "closed";
  }

  get isActive(): boolean {
    return this.isSigned || this.isExpiring || this.isEnding;
  }

  get updatedAt(): string {
    return this.data.updatedAt;
  }

  get updatedBy(): any {
    return this.data.updatedBy;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  public wouldRunAt(date: Date): boolean {
    if (this.lastDate === null) {
      return true;
    }

    return date.getTime() <= this.lastDate.getTime() && date.getTime() > this.firstDate.getTime();
  }

  public getRentalChargeTemplate(): ChargeTemplateInterface | undefined {
    return this.chargeTemplates.find((chargeTemplate: ChargeTemplateInterface) => {
      return chargeTemplate.type.id === TypeContainer.pickIdFromLabel("Basic_rent");
    });
  }

  public getDateForExpirationNotice(): Date | null {
    return this.expirationDate;
  }

  get note(): string {
    return this.data.note;
  }

  public renderAvatar(params: any = {}): any {
    return Image.getAvatarAbsolutely(this.data.avatar, "contract", params);
  }

  get paidStatus(): boolean {
    return this.data.paidStatus;
  }

  get defaultAgencyFees(): AgencyFeeInterface[] {
    return this.all("app.agencyFees")
      .filter((agencyFee: AgencyFeeInterface) =>
        agencyFee.removed === false &&
        agencyFee.objectType === AgencyFeeContainer.pickIdFromLabel("Contract") &&
        agencyFee.objectId === this.data.id);
  }

  get inheritedAgencyFee(): AgencyFeeInterface | null {
    if (this.unit && this.unit.defaultAgencyFees.length > 0) {
      return this.unit.defaultAgencyFees[0];
    }

    if (this.unit && this.unit.property && this.unit.property.defaultAgencyFees.length > 0) {
      return this.unit.property.defaultAgencyFees[0];
    }

    if (this.unit && this.unit.property && this.unit.property.company && this.unit.property.company.defaultAgencyFees.length > 0) {
      return this.unit.property.company.defaultAgencyFees[0];
    }

    if (this.unit && this.unit.property && this.unit.property.company && this.unit.property.company.agency && this.unit.property.company.agency.defaultAgencyFees.length > 0) {
      return this.unit.property.company.agency.defaultAgencyFees[0];
    }

    return null;
  }

  get autoSendInvoiceToTenant(): boolean {
    return this.data.autoSendInvoiceToTenant;
  }

  get isCommercial(): boolean {
    return this.data.isCommercial;
  }

  get isReadonly(): boolean {
    return this.removed ||
      !this.isConcept;
  }

  get addressForRentEmail(): string {
    return this.data.addressForRentEmail;
  }

  get financialStartDate(): Date {
    return this.unit.property.company.financialStartDate;
  }

  get ownerName(): string {
    return this.unit.property.company.name;
  }
}

export { Contract };
